import {
  COMMAND_TYPE,
  DEVICE_STATUS,
  DEVICE_STATUS_SORT_ORDER,
  SUBSCRIPTION_STATUS,
  type CommandType,
} from './constants';
import type { Device } from './schemas';

// Pure device rules.
//
// Most of these now *read* a server decision rather than deriving one: the live API computes
// `powerIcon.{visible,enabled,action}` and `cardState` per row, so the client is not entitled to
// its own opinion. The status-derived fallbacks below apply only to devices built without a
// server payload (tests, map fixtures) — they are kept because the AC's decision tables are
// still the contract those server fields are expected to honour, and asserting them here is how
// we notice if the server stops.

/**
 * Sort rank for the default grid order (US-112439 §1). `expired` is folded into `status` by
 * `toDevice`, so it is just another rank here.
 *
 * The **server** owns the real ordering — its cursor carries a `statusPriority` and pages arrive
 * pre-sorted. This exists for tests and the mock, not to re-sort a delivered page.
 */
export function sortRank(device: Device): number {
  return DEVICE_STATUS_SORT_ORDER[device.status];
}

/** Status group ascending, then Last Sync descending. Never-synced sorts last in its group. */
export function compareDevices(a: Device, b: Device): number {
  const rankDelta = sortRank(a) - sortRank(b);
  if (rankDelta !== 0) return rankDelta;

  const aSync = a.lastSyncedAt ? Date.parse(a.lastSyncedAt) : Number.NEGATIVE_INFINITY;
  const bSync = b.lastSyncedAt ? Date.parse(b.lastSyncedAt) : Number.NEGATIVE_INFINITY;
  return bSync - aSync;
}

/**
 * Whether the whole card is non-interactive.
 *
 * Prefers the server's `cardState`. The fallback is the AC's rule: Pending QC / Pending
 * Configuration disable the card (§8), while Expired stays clickable and only loses its power
 * control (§17).
 */
export function isCardDisabled(device: Device): boolean {
  if (device.powerIcon || device.cardDisabled) return device.cardDisabled;
  return (
    device.status === DEVICE_STATUS.PENDING_QC ||
    device.status === DEVICE_STATUS.PENDING_CONFIGURATION
  );
}

export type PowerControl =
  /** No power icon at all. */
  | { kind: 'hidden' }
  /** Shown but greyed: pending states, expired subscription, or a command already queued. */
  | { kind: 'disabled'; command: CommandType | null }
  /** Live; clicking queues `command` with no confirmation dialog. */
  | { kind: 'enabled'; command: CommandType };

/**
 * The power-icon state (US-112439 §6, US-112437 §8).
 *
 * Server-first: `powerIcon.visible` decides whether the disc appears, `powerIcon.enabled`
 * whether it is clickable, and `powerIcon.action` which command a click queues. `action` also
 * reveals the live state — the only command ever offered on a running device is Turn Off.
 *
 * A queued command always wins over `enabled`: only one command may be queued at a time (§6,
 * §7), so the disc greys out even if the server has not caught up.
 *
 * The fallback path can no longer key off a device *type*: the backend carries no
 * diesel/electric/sensor flag (`category` is a free-form per-account name), so the AC's
 * "diesel cannot be turned on remotely" rule is only expressible as `powerIcon.action === null`
 * coming from the server. Flagged in the spec.
 */
export function powerControl(device: Device): PowerControl {
  if (device.powerIcon) {
    if (!device.powerIcon.visible) return { kind: 'hidden' };
    if (device.queuedCommand) {
      return { kind: 'disabled', command: device.queuedCommand.type };
    }
    if (!device.powerIcon.enabled || !device.powerIcon.action) {
      return { kind: 'disabled', command: device.powerIcon.action };
    }
    return { kind: 'enabled', command: device.powerIcon.action };
  }

  // ── Fallback: no server payload (tests, fixtures) ──
  const live = effectiveLiveStatus(device);
  if (live !== DEVICE_STATUS.RUNNING && live !== DEVICE_STATUS.OFF) {
    return { kind: 'disabled', command: null };
  }
  const command = live === DEVICE_STATUS.RUNNING ? COMMAND_TYPE.TURN_OFF : COMMAND_TYPE.TURN_ON;
  if (device.queuedCommand) return { kind: 'disabled', command: device.queuedCommand.type };
  if (device.subscriptionStatus === SUBSCRIPTION_STATUS.EXPIRED) {
    return { kind: 'disabled', command };
  }
  return { kind: 'enabled', command };
}

/**
 * The live state the power disc reflects. While Disconnected the AC wants the *last known*
 * status so the right action is still offered; the API expresses that through
 * `powerIcon.action` rather than a separate field, so there is nothing to fall back to beyond
 * Off — which offers the safe Turn On.
 */
export function effectiveLiveStatus(device: Device): Device['status'] {
  if (device.powerIcon?.action) {
    return device.powerIcon.action === COMMAND_TYPE.TURN_OFF
      ? DEVICE_STATUS.RUNNING
      : DEVICE_STATUS.OFF;
  }
  if (device.status !== DEVICE_STATUS.DISCONNECTED) return device.status;
  return DEVICE_STATUS.OFF;
}

/**
 * Whether the power disc renders in its lit (accent-yellow, glowing) colorway, and therefore
 * whether the card gets its bottom wash.
 *
 * This tracks the device's **live state**, not whether the control is clickable — the design is
 * explicit about the split: a Running device with a Turn Off already queued keeps a bright disc
 * (Figma 232:355224), while an Off device with a Turn On queued shows a grey one. Read plainly:
 * the disc is lit when the pump is running. An expired subscription is always grey.
 */
export function isPowerEnergised(device: Device): boolean {
  if (powerControl(device).kind === 'hidden') return false;
  if (device.subscriptionStatus === SUBSCRIPTION_STATUS.EXPIRED) return false;
  return effectiveLiveStatus(device) === DEVICE_STATUS.RUNNING;
}

/**
 * Headline for the *details* header, where the grower's custom name leads and the
 * default/serial name sits beneath it (US-112437 §1). The listing card shows the default name
 * only — the AC's example there is the model ("Hydro Multi-E 2 CRE").
 */
export function detailHeading(device: Device): string {
  return device.customName?.trim() ? device.customName : device.name;
}
